import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import {
  checkReceiptsAccess,
  checkReceiptsAdminAccess,
  hasReceiptsAccessFromContextUser,
  hasReceiptsAdminFromContextUser
} from '../utils/receiptsAccess';
import {
  checkContractsAccess,
  hasContractsAccessFromContextUser
} from '../utils/contractsAccess';
import { normalizeLocationsClaims, fetchMergedUserRouteAccess, userHasLocationAccess, routeTierBypassAllowsLocation, userHasRouteTierBypass, claimsAllowClientRecords, canViewReportingFromUser } from '../utils/roleUtils';
import {
  consumeLogoutRedirectPath,
  peekLogoutRedirectPath,
  peekRequireCredentialReauth
} from '../utils/authSession';
import { guestPermissionsFromClaims, isGuestAccount } from '../utils/guestAuth';

const guestPermissionClaimMap = {
  clientProfile: 'guestClientProfile',
  addClient: 'guestAddClient',
  programs: 'guestPrograms'
};

const ProtectedRoute = ({
  requiredLocations = [],
  allowCompliance = false,
  requireReceiptsAccess = false,
  requireReceiptsAdmin = false,
  requireContractsAccess = false,
  guestPermission = null,
  allowAuthenticatedGuest = false,
  requireClientListAccess = false,
  requireReportingAccess = false,
  requireLocationTag = false,
  children
}) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const needsReceiptsCheck = requireReceiptsAccess || requireReceiptsAdmin;
  const needsContractsCheck = requireContractsAccess;
  const needsPrivilegedCheck = needsReceiptsCheck || needsContractsCheck;
  const syncReceiptsAccess = hasReceiptsAccessFromContextUser(currentUser);
  const syncReceiptsAdmin = hasReceiptsAdminFromContextUser(currentUser);
  const syncReceiptsFullyAllowed =
    (!requireReceiptsAccess || syncReceiptsAccess) &&
    (!requireReceiptsAdmin || syncReceiptsAdmin);
  const [receiptsCheckDone, setReceiptsCheckDone] = useState(
    !needsReceiptsCheck || syncReceiptsFullyAllowed
  );
  const [hasReceiptsAccess, setHasReceiptsAccess] = useState(syncReceiptsAccess);
  const [hasReceiptsAdminAccess, setHasReceiptsAdminAccess] = useState(
    syncReceiptsAdmin || !requireReceiptsAdmin
  );
  const syncContractsAccess = hasContractsAccessFromContextUser(currentUser);
  const [contractsCheckDone, setContractsCheckDone] = useState(
    !needsContractsCheck || syncContractsAccess
  );
  const [hasContractsAccess, setHasContractsAccess] = useState(syncContractsAccess);
  const needsLocationCheck = requiredLocations.length > 0;
  const [mergedRouteLocations, setMergedRouteLocations] = useState([]);
  const [firestoreRole, setFirestoreRole] = useState(null);
  const [locationCheckDone, setLocationCheckDone] = useState(!needsLocationCheck);

  useEffect(() => {
    if (!needsLocationCheck) {
      setLocationCheckDone(true);
      return undefined;
    }

    if (loading) {
      return undefined;
    }

    if (!currentUser) {
      setLocationCheckDone(true);
      return undefined;
    }

    const fallback = normalizeLocationsClaims(
      currentUser.claims?.locations || currentUser.locations || []
    );
    setMergedRouteLocations(fallback);
    setFirestoreRole(null);
    setLocationCheckDone(false);

    let cancelled = false;
    const authUser =
      auth.currentUser || (typeof currentUser.getIdToken === 'function' ? currentUser : null);
    if (!authUser) {
      setLocationCheckDone(true);
      return undefined;
    }

    fetchMergedUserRouteAccess(authUser)
      .then(({ locations, firestoreRole: profileRole }) => {
        if (!cancelled) {
          setMergedRouteLocations(locations);
          setFirestoreRole(profileRole);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMergedRouteLocations(fallback);
          setFirestoreRole(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLocationCheckDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [needsLocationCheck, loading, currentUser]);

  useEffect(() => {
    if (!needsReceiptsCheck) {
      return undefined;
    }

    if (loading) {
      setReceiptsCheckDone(false);
      return undefined;
    }

    const syncAccess = hasReceiptsAccessFromContextUser(currentUser);
    const syncAdmin = hasReceiptsAdminFromContextUser(currentUser);

    if (syncAccess && (!requireReceiptsAdmin || syncAdmin)) {
      setHasReceiptsAccess(true);
      setHasReceiptsAdminAccess(syncAdmin || !requireReceiptsAdmin);
      setReceiptsCheckDone(true);
      return undefined;
    }

    let cancelled = false;
    setReceiptsCheckDone(false);
    if (syncAccess) {
      setHasReceiptsAccess(true);
    } else {
      setHasReceiptsAccess(false);
    }
    if (syncAdmin) {
      setHasReceiptsAdminAccess(true);
    } else if (!syncAccess) {
      setHasReceiptsAdminAccess(false);
    }

    const authUser =
      auth.currentUser || (typeof currentUser?.getIdToken === 'function' ? currentUser : null);
    if (!authUser) {
      if (!currentUser) {
        setHasReceiptsAccess(false);
        setHasReceiptsAdminAccess(false);
        setReceiptsCheckDone(true);
      } else if (syncAccess) {
        setHasReceiptsAccess(true);
        setHasReceiptsAdminAccess(syncAdmin || !requireReceiptsAdmin);
        setReceiptsCheckDone(true);
      }
      return undefined;
    }

    const accessCheck = requireReceiptsAccess
      ? checkReceiptsAccess(authUser)
      : Promise.resolve(true);
    const adminCheck = requireReceiptsAdmin
      ? checkReceiptsAdminAccess(authUser)
      : Promise.resolve(true);

    Promise.all([accessCheck, adminCheck])
      .then(([access, adminAccess]) => {
        if (cancelled) {
          return;
        }
        setHasReceiptsAccess(access || syncAccess);
        setHasReceiptsAdminAccess(adminAccess || syncAdmin);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setHasReceiptsAccess(syncAccess);
        setHasReceiptsAdminAccess(syncAdmin);
      })
      .finally(() => {
        if (!cancelled) {
          setReceiptsCheckDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    needsReceiptsCheck,
    requireReceiptsAccess,
    requireReceiptsAdmin,
    loading,
    currentUser
  ]);

  useEffect(() => {
    if (!needsContractsCheck) {
      return undefined;
    }

    if (loading) {
      setContractsCheckDone(false);
      return undefined;
    }

    const syncAccess = hasContractsAccessFromContextUser(currentUser);
    if (syncAccess) {
      setHasContractsAccess(true);
      setContractsCheckDone(true);
      return undefined;
    }

    let cancelled = false;
    setContractsCheckDone(false);
    setHasContractsAccess(false);

    const authUser =
      auth.currentUser || (typeof currentUser?.getIdToken === 'function' ? currentUser : null);
    if (!authUser) {
      if (!currentUser) {
        setHasContractsAccess(false);
        setContractsCheckDone(true);
      } else if (syncAccess) {
        setHasContractsAccess(true);
        setContractsCheckDone(true);
      }
      return undefined;
    }

    checkContractsAccess(authUser)
      .then((access) => {
        if (cancelled) {
          return;
        }
        setHasContractsAccess(access || syncAccess);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setHasContractsAccess(syncAccess);
      })
      .finally(() => {
        if (!cancelled) {
          setContractsCheckDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [needsContractsCheck, loading, currentUser]);

  const allowedReceipts = hasReceiptsAccess || syncReceiptsAccess;
  const allowedReceiptsAdmin = hasReceiptsAdminAccess || syncReceiptsAdmin;
  const allowedContracts = hasContractsAccess || syncContractsAccess;

  if (peekRequireCredentialReauth()) {
    const logoutRedirect = peekLogoutRedirectPath();
    return (
      <Navigate
        to={logoutRedirect || '/login'}
        state={{
          from: location,
          signedOut: !logoutRedirect
        }}
        replace={true}
      />
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  // After auth settles, require a context user. Do not spin forever on
  // auth.currentUser && !currentUser (that mismatch previously required a hard refresh).
  if (!currentUser) {
    const logoutRedirect = consumeLogoutRedirectPath();
    return (
      <Navigate
        to={logoutRedirect || '/login'}
        state={{
          from: location,
          signedOut: !logoutRedirect
        }}
        replace={true}
      />
    );
  }

  if (
    (needsPrivilegedCheck && !receiptsCheckDone && !syncReceiptsFullyAllowed && needsReceiptsCheck) ||
    (needsContractsCheck && !contractsCheckDone && !syncContractsAccess) ||
    (needsLocationCheck && !locationCheckDone)
  ) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (requireReceiptsAccess && !allowedReceipts) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requireReceiptsAdmin && !allowedReceiptsAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requireContractsAccess && !allowedContracts) {
    return <Navigate to="/unauthorized" replace />;
  }

  const user = currentUser || (auth.currentUser ? {
    ...auth.currentUser,
    uid: auth.currentUser.uid,
    email: auth.currentUser.email,
    displayName: auth.currentUser.displayName || '',
    claims: {},
    locations: [],
    isAdmin: false,
    isStaff: false,
    isManager: false,
    isExecutive: false,
    isCompliance: false,
    isGuest: false
  } : null);

  const userLocations = mergedRouteLocations.length > 0
    ? mergedRouteLocations
    : normalizeLocationsClaims(user?.claims?.locations || user?.locations || []);
  const isGuest = isGuestAccount(user);

  if (isGuest) {
    const guestPermissions = guestPermissionsFromClaims(user?.claims || {});

    if (guestPermission) {
      const claimKey = guestPermissionClaimMap[guestPermission];
      if (!claimKey || user?.claims?.[claimKey] !== true) {
        return <Navigate to="/unauthorized" replace />;
      }
    } else if (requiredLocations.length > 0) {
      if (!guestPermissions.programs) {
        return <Navigate to="/unauthorized" replace />;
      }
    } else if (!allowAuthenticatedGuest) {
      return <Navigate to="/unauthorized" replace />;
    }

    if (requiredLocations.length > 0) {
      const hasLocation = requiredLocations.some((loc) => userHasLocationAccess(userLocations, loc));
      if (!hasLocation) {
        return <Navigate to="/unauthorized" replace />;
      }
    }

    return children;
  }

  if (requireClientListAccess && !claimsAllowClientRecords(user?.claims || {})) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requireReportingAccess && !canViewReportingFromUser(user)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredLocations.length > 0 && user) {
    const hasLocation = requiredLocations.some((loc) => userHasLocationAccess(userLocations, loc));
    const tier = userHasRouteTierBypass(user, firestoreRole);
    const tierAllows = requireLocationTag
      ? tier.isAdmin || tier.isExecutive
      : routeTierBypassAllowsLocation(user, firestoreRole, allowCompliance);

    if (!hasLocation && !tierAllows) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
